import type { PDFDocument, PDFFont, PDFPage } from 'pdf-lib';
import { CustomReportFieldDto, CustomReportFieldType } from 'providers/api';
import { parsePdfAnnotations, CustomReportPdfAnnotation } from './customReportPdfAnnotations';

interface ExportFilledCustomReportPdfOptions {
  pdfBlob: Blob;
  reportName: string;
  fields: CustomReportFieldDto[];
  values: Record<string, string>;
  pageNumbers?: number[];
}

interface PdfBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

type PdfLib = typeof import('pdf-lib');

const safeFileName = (value: string) => (
  value
    .trim()
    .replace(/[^a-z0-9-_]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    || 'custom-report'
);

const hexToRgb = (hex?: string) => {
  if (!hex) return undefined;
  const normalised = hex.trim().replace('#', '');
  const full = normalised.length === 3
    ? normalised.split('').map((char) => char + char).join('')
    : normalised;
  if (!/^[0-9a-f]{6}$/i.test(full)) return undefined;

  return {
    r: parseInt(full.slice(0, 2), 16) / 255,
    g: parseInt(full.slice(2, 4), 16) / 255,
    b: parseInt(full.slice(4, 6), 16) / 255,
  };
};

const displayValue = (field: CustomReportFieldDto, value?: string) => {
  if (field.type === CustomReportFieldType.Checkbox) {
    return value === 'true' ? 'Yes' : 'No';
  }

  return value ?? '';
};

const wrapText = (text: string, font: PDFFont, fontSize: number, maxWidth: number) => {
  const words = text.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  if (words.length === 0) return [''];

  return words.reduce<string[]>((lines, word) => {
    const currentLine = lines[lines.length - 1] ?? '';
    const nextLine = currentLine ? `${currentLine} ${word}` : word;

    if (font.widthOfTextAtSize(nextLine, fontSize) <= maxWidth || currentLine.length === 0) {
      return [...lines.slice(0, -1), nextLine];
    }

    return [...lines, word];
  }, ['']);
};

const placementToBox = (field: CustomReportFieldDto, page: PDFPage): PdfBox | undefined => {
  if (!field.pdfPlacement) return undefined;

  const {
    x: placementX,
    y: placementY,
    width: placementWidth,
    height: placementHeight,
  } = field.pdfPlacement;

  if (
    placementX === undefined
    || placementY === undefined
    || placementWidth === undefined
    || placementHeight === undefined
  ) {
    return undefined;
  }

  const { width: pageWidth, height: pageHeight } = page.getSize();
  const width = ((placementWidth ?? 0) / 100) * pageWidth;
  const height = ((placementHeight ?? 0) / 100) * pageHeight;

  return {
    x: ((placementX ?? 0) / 100) * pageWidth,
    y: pageHeight - (((placementY ?? 0) / 100) * pageHeight) - height,
    width,
    height,
  };
};

const annotationToBox = (annotation: CustomReportPdfAnnotation, page: PDFPage): PdfBox => {
  const { width: pageWidth, height: pageHeight } = page.getSize();
  const width = (annotation.width / 100) * pageWidth;
  const height = (annotation.height / 100) * pageHeight;

  return {
    x: (annotation.x / 100) * pageWidth,
    y: pageHeight - ((annotation.y / 100) * pageHeight) - height,
    width,
    height,
  };
};

const drawCheckbox = (page: PDFPage, box: PdfBox, checked: boolean, pdfLib: PdfLib) => {
  const size = Math.min(box.width, box.height) * 0.8;
  const x = box.x + (box.width - size) / 2;
  const y = box.y + (box.height - size) / 2;

  page.drawRectangle({
    x,
    y,
    width: size,
    height: size,
    borderColor: pdfLib.rgb(0, 0, 0),
    borderWidth: 1,
  });

  if (!checked) return;

  page.drawLine({
    start: { x: x + size * 0.2, y: y + size * 0.52 },
    end: { x: x + size * 0.42, y: y + size * 0.25 },
    thickness: Math.max(1, size * 0.08),
    color: pdfLib.rgb(0, 0, 0),
  });
  page.drawLine({
    start: { x: x + size * 0.42, y: y + size * 0.25 },
    end: { x: x + size * 0.82, y: y + size * 0.78 },
    thickness: Math.max(1, size * 0.08),
    color: pdfLib.rgb(0, 0, 0),
  });
};

const getImageData = async (imageSource: string) => {
  const response = await fetch(imageSource);
  return {
    contentType: response.headers.get('content-type') ?? '',
    bytes: await response.arrayBuffer(),
  };
};

const drawImageAnnotation = async (
  pdfDoc: PDFDocument,
  page: PDFPage,
  box: PdfBox,
  imageSource: string,
  pdfLib: PdfLib,
) => {
  if (!imageSource) return;

  const { contentType, bytes } = await getImageData(imageSource);
  const image = contentType.includes('png')
    ? await pdfDoc.embedPng(bytes)
    : await pdfDoc.embedJpg(bytes);
  const scale = Math.min(box.width / image.width, box.height / image.height);
  const width = image.width * scale;
  const height = image.height * scale;

  page.drawRectangle({
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
    color: pdfLib.rgb(1, 1, 1),
  });
  page.drawImage(image, {
    x: box.x + (box.width - width) / 2,
    y: box.y + (box.height - height) / 2,
    width,
    height,
  });
};

const drawFieldValue = (page: PDFPage, field: CustomReportFieldDto, value: string, font: PDFFont, pdfLib: PdfLib) => {
  const box = placementToBox(field, page);
  if (!box) return;

  if (field.type === CustomReportFieldType.Checkbox) {
    drawCheckbox(page, box, value === 'true', pdfLib);
    return;
  }

  const padding = 3;
  const fontSize = Math.max(6, Math.min(12, box.height * 0.42));
  const lineHeight = fontSize * 1.2;
  const maxLines = Math.max(1, Math.floor((box.height - padding * 2) / lineHeight));
  const lines = wrapText(displayValue(field, value), font, fontSize, Math.max(1, box.width - padding * 2)).slice(0, maxLines);
  const firstLineY = box.y + box.height - padding - fontSize;

  lines.forEach((line, index) => {
    page.drawText(line, {
      x: box.x + padding,
      y: firstLineY - index * lineHeight,
      size: fontSize,
      font,
      color: pdfLib.rgb(0, 0, 0),
    });
  });
};

const drawAnnotation = (
  pdfDoc: PDFDocument,
  page: PDFPage,
  annotation: CustomReportPdfAnnotation,
  font: PDFFont,
  signatureFont: PDFFont,
  pdfLib: PdfLib,
) => {
  const box = annotationToBox(annotation, page);

  if (annotation.type === 'image') {
    return drawImageAnnotation(pdfDoc, page, box, annotation.value, pdfLib);
  }

  if (annotation.type === 'whiteout') {
    page.drawRectangle({
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height,
      color: pdfLib.rgb(1, 1, 1),
    });
    return Promise.resolve();
  }

  if (annotation.type === 'checkbox') {
    drawCheckbox(page, box, annotation.value === 'true', pdfLib);
    return Promise.resolve();
  }

  const targetFont = annotation.type === 'signature' ? signatureFont : font;
  const padding = 3;
  const fontSize = annotation.fontSize && annotation.fontSize > 0
    ? Math.max(6, Math.min(48, annotation.fontSize))
    : Math.max(6, Math.min(annotation.type === 'signature' ? 18 : 12, box.height * (annotation.type === 'signature' ? 0.58 : 0.42)));
  const lineHeight = fontSize * 1.2;
  const maxLines = Math.max(1, Math.floor((box.height - padding * 2) / lineHeight));
  const lines = wrapText(annotation.value, targetFont, fontSize, Math.max(1, box.width - padding * 2)).slice(0, maxLines);
  const firstLineY = box.y + box.height - padding - fontSize;
  const textColor = hexToRgb(annotation.color) ?? { r: 0, g: 0, b: 0 };

  lines.forEach((line, index) => {
    page.drawText(line, {
      x: box.x + padding,
      y: firstLineY - index * lineHeight,
      size: fontSize,
      font: targetFont,
      color: pdfLib.rgb(textColor.r, textColor.g, textColor.b),
    });
  });

  return Promise.resolve();
};

const addSummaryPage = (
  pdfDoc: PDFDocument,
  reportName: string,
  fields: CustomReportFieldDto[],
  values: Record<string, string>,
  font: PDFFont,
  boldFont: PDFFont,
  pdfLib: PdfLib,
) => {
  if (fields.length === 0) return;

  let page = pdfDoc.addPage();
  let { width, height } = page.getSize();
  const margin = 48;
  const lineHeight = 14;
  let y = height - margin;

  const ensureSpace = (requiredHeight: number) => {
    if (y - requiredHeight >= margin) return;

    page = pdfDoc.addPage();
    ({ width, height } = page.getSize());
    y = height - margin;
  };

  page.drawText(`${reportName} - Additional Fields`, {
    x: margin,
    y,
    size: 14,
    font: boldFont,
    color: pdfLib.rgb(0, 0, 0),
  });
  y -= 24;

  fields.forEach((field) => {
    const value = displayValue(field, values[field.id ?? '']);
    const valueLines = wrapText(value || '-', font, 10, width - margin * 2);
    const label = field.label ?? '';
    ensureSpace(lineHeight * (valueLines.length + 2));

    page.drawText(label ?? '', {
      x: margin,
      y,
      size: 10,
      font: boldFont,
      color: pdfLib.rgb(0, 0, 0),
    });
    y -= lineHeight;

    valueLines.forEach((line) => {
      page.drawText(line, {
        x: margin,
        y,
        size: 10,
        font,
        color: pdfLib.rgb(0, 0, 0),
      });
      y -= lineHeight;
    });

    y -= 4;
  });
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const removeUnselectedPages = (pdfDoc: PDFDocument, pageNumbers?: number[]) => {
  if (!pageNumbers?.length) return;

  const keepIndexes = new Set(
    pageNumbers
      .map((pageNumber) => Math.max(1, Math.floor(pageNumber)) - 1)
      .filter((pageIndex) => pageIndex >= 0 && pageIndex < pdfDoc.getPageCount()),
  );

  if (keepIndexes.size === 0) return;

  for (let pageIndex = pdfDoc.getPageCount() - 1; pageIndex >= 0; pageIndex -= 1) {
    if (!keepIndexes.has(pageIndex)) {
      pdfDoc.removePage(pageIndex);
    }
  }
};

export const buildFilledCustomReportPdfBlob = async ({
  pdfBlob,
  reportName,
  fields,
  values,
  pageNumbers,
}: ExportFilledCustomReportPdfOptions): Promise<Blob> => {
  const pdfLib = await import('pdf-lib');
  const pdfDoc = await pdfLib.PDFDocument.load(await pdfBlob.arrayBuffer());
  const font = await pdfDoc.embedFont(pdfLib.StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(pdfLib.StandardFonts.HelveticaBold);
  const signatureFont = await pdfDoc.embedFont(pdfLib.StandardFonts.TimesRomanItalic);
  const pages = pdfDoc.getPages();
  const annotations = parsePdfAnnotations(values);

  fields
    .filter((field) => field.pdfPlacement)
    .forEach((field) => {
      const pageIndex = Math.max(0, (field.pdfPlacement?.page ?? 1) - 1);
      const page = pages[pageIndex];
      if (!page || !field.id) return;

      drawFieldValue(page, field, values[field.id ?? ''] ?? '', font, pdfLib);
    });

  await annotations.reduce<Promise<void>>(async (previous, annotation) => {
    await previous;
    const pageIndex = Math.max(0, annotation.page - 1);
    const page = pages[pageIndex];
    if (!page) return;

    await drawAnnotation(pdfDoc, page, annotation, font, signatureFont, pdfLib);
  }, Promise.resolve());

  removeUnselectedPages(pdfDoc, pageNumbers);

  addSummaryPage(
    pdfDoc,
    reportName,
    fields.filter((field) => !field.pdfPlacement),
    values,
    font,
    boldFont,
    pdfLib,
  );

  const pdfBytes = await pdfDoc.save();
  return new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
};

const exportFilledCustomReportPdf = async (options: ExportFilledCustomReportPdfOptions) => {
  const blob = await buildFilledCustomReportPdfBlob(options);
  downloadBlob(blob, `${safeFileName(options.reportName)}-filled.pdf`);
};

export default exportFilledCustomReportPdf;
